import { useEffect, useState } from 'react';
import { collection, doc, onSnapshot } from 'firebase/firestore';
import { COLLECTIONS, type AssetDoc, type CampaignDoc } from '@brands/shared';
import { firebase } from '../lib/firebase';

export type Loadable<T> =
  | { status: 'loading' }
  | { status: 'ready'; data: T }
  | { status: 'missing' }
  | { status: 'error'; message: string };

export type Asset = AssetDoc & { id: string };

export function useCampaign(id: string | undefined): Loadable<CampaignDoc> {
  const [state, setState] = useState<Loadable<CampaignDoc>>({ status: 'loading' });
  useEffect(() => {
    if (!id) return;
    return onSnapshot(
      doc(firebase().db, COLLECTIONS.campaigns, id),
      (snap) =>
        setState(
          snap.exists()
            ? { status: 'ready', data: snap.data() as CampaignDoc }
            : { status: 'missing' },
        ),
      () => setState({ status: 'error', message: 'No pudimos cargar la campaña.' }),
    );
  }, [id]);
  return state;
}

export function useAssets(campaignId: string | undefined): Loadable<Asset[]> {
  const [state, setState] = useState<Loadable<Asset[]>>({ status: 'loading' });
  useEffect(() => {
    if (!campaignId) return;
    return onSnapshot(
      collection(firebase().db, COLLECTIONS.campaigns, campaignId, COLLECTIONS.assets),
      (snap) => {
        const assets = snap.docs.map((d) => ({ ...(d.data() as AssetDoc), id: d.id }));
        assets.sort(
          (a, b) =>
            a.path.localeCompare(b.path, 'es') ||
            a.name.localeCompare(b.name, 'es', { numeric: true }),
        );
        setState({ status: 'ready', data: assets });
      },
      () => setState({ status: 'error', message: 'No pudimos cargar los archivos.' }),
    );
  }, [campaignId]);
  return state;
}

/** Agrupa por ruta de subcarpeta conservando el orden. */
export function groupByPath(assets: Asset[]): { path: string; assets: Asset[] }[] {
  const groups = new Map<string, Asset[]>();
  for (const a of assets) {
    const list = groups.get(a.path) ?? [];
    list.push(a);
    groups.set(a.path, list);
  }
  return [...groups.entries()].map(([path, list]) => ({ path, assets: list }));
}
